(() => {
  'use strict';
  if (customElements.get('predictive-search')) return;
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this.ready || this.dataset.enabled !== 'true') return;
      this.ready = true;
      this.input = this.querySelector('input[type=search]');
      this.results = this.querySelector('.predictive-results');
      this.status = this.querySelector('[data-search-status]');
      if (!this.input || !this.results) return;
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.input.addEventListener(
        'input',
        () => {
          clearTimeout(this.timer);
          this.pending?.abort();
          this.index = -1;
          this.close();
          this.timer = setTimeout(() => this.search(), 230);
        },
        options,
      );
      this.input.addEventListener('keydown', (event) => this.onKey(event), options);
      this.addEventListener(
        'focusout',
        () => {
          this.focusTimer = setTimeout(() => {
            if (!this.contains(document.activeElement)) this.close();
          }, 0);
        },
        options,
      );
      this.input.addEventListener(
        'focus',
        () => {
          if (this.results.children.length && this.lastQuery === this.input.value.trim()) this.open();
        },
        options,
      );
      this.results.addEventListener(
        'pointerdown',
        (event) => {
          if (event.target.closest('a')) event.preventDefault();
        },
        options,
      );
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.focusTimer);
      this.pending?.abort();
      this.listeners?.abort();
      this.ready = false;
    }
    open() {
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }
    close() {
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
    }
    async search() {
      const query = this.input.value.trim();
      if (!query) {
        this.results.replaceChildren();
        return;
      }
      this.pending?.abort();
      this.pending = new AbortController();
      const root = (window.Shopify?.routes?.root || document.body.dataset.root || '/').replace(/\/?$/, '/');
      const brandQuery = window.dlfBrandSearch?.query(query);
      const url = new URL(root + (brandQuery ? 'search' : 'search/suggest'), location.origin);
      url.search = new URLSearchParams({
        q: brandQuery || query,
        type: 'product',
        'resources[type]': 'product,collection,query',
        'resources[limit]': '5',
        section_id: 'predictive-search',
      }).toString();
      try {
        const response = await fetch(url, { signal: this.pending.signal });
        if (!response.ok) throw new Error('Search unavailable');
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const result = parsed.getElementById('shopify-section-predictive-search');
        if (!result || this.input.value.trim() !== query) return;
        result.querySelectorAll('a[href]').forEach(link => window.dlfBrandSearch?.prepareLink(link));
        if (brandQuery) {
          const viewAll = result.querySelector('.predictive-view-all');
          if (viewAll) {
            const target = new URL(viewAll.href, location.href);
            target.searchParams.set('q', query);
            viewAll.href = target.href;
            viewAll.textContent = viewAll.textContent.replace(/:\s*.*$/, ': ' + query);
          }
        }
        this.results.innerHTML = result.innerHTML;
        this.options = [...this.results.querySelectorAll('[role=option]')];
        this.options.forEach((el, i) => {
          el.id = this.results.id + '-option-' + i;
        });
        this.index = -1;
        this.lastQuery = query;
        this.status.textContent =
          this.options.length +
          ' ' +
          (this.results.querySelector('[role=listbox]')?.getAttribute('aria-label') || query);
        this.open();
      } catch (error) {
        if (error.name !== 'AbortError') this.close();
      }
    }
    onKey(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (this.results.hidden) return;
      if (event.key === 'Enter' && this.index >= 0) {
        event.preventDefault();
        this.options[this.index]?.querySelector('a')?.click();
        return;
      }
      if (!['ArrowDown', 'ArrowUp'].includes(event.key) || !this.options?.length) return;
      event.preventDefault();
      this.index =
        (this.index + (event.key === 'ArrowDown' ? 1 : -1) + this.options.length) % this.options.length;
      this.options.forEach((el, i) => el.setAttribute('aria-selected', String(i === this.index)));
      const active = this.options[this.index];
      this.input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    }
  }
  customElements.define('predictive-search', PredictiveSearch);
})();
